/**
 * Live Telemetry Page Component - Refactored with SOLID Principles
 * 
 * Single Responsibility: Renders live telemetry UI
 * Uses custom hooks for data fetching and calculations (Separation of Concerns)
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { StrategyCalculatorFactory } from "../utils/strategyCalculator";
import type { Strategy, RaceEvent, DriverData, PitStopDetails } from "../types/strategy";
import { StrategyResult } from "../components/StrategyResult";
import { NumberInput } from "../components/NumberInput";
import { LiveStrategyOptions } from "../components/LiveStrategyOptions";
import { LivePlanTrackerPanel } from "../components/LivePlanTrackerPanel";
import { RaceStrategyPanel } from "../components/RaceStrategyPanel";
import type { RacePlan, StaticStrategyInput } from "../utils/staticStrategyPlanner";

// Type definitions for telemetry data
interface SessionStatus {
  simConnected: boolean;
  sessionType: string | null;
  trackName: string | null;
  lastUpdate: string;
}

interface SessionData {
  raceDurationMin?: number;
  fuelCapacity?: number;
  isMulticlass?: boolean;
  fastestClassLapTime?: number;
  yourCarClassShortName?: string;
  sessionTimeRemain?: number;
  p1LapTime?: number;
  p1AvgLapTime?: number;
}

interface LapData {
  lapNumber: number;
  lapTime: number | null;
  fuelLevel: number | null;
  fuelUsed?: number;
}

/**
 * Format seconds to MM:SS.mmm
 */
function formatLapTime(seconds: number | null): string {
  if (seconds === null || seconds === 0) return '--:--.---';
  
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

/**
 * Format fuel level to 2 decimal places
 */
function formatFuel(liters: number | null): string {
  if (liters === null) return '--';
  return liters.toFixed(2);
}

interface LiveTelemetryPageProps {
  selectedPlan?: RacePlan | null;
  selectedPlanInput?: StaticStrategyInput | null;
  onClearPlan?: () => void;
}

export const LiveTelemetryPage: React.FC<LiveTelemetryPageProps> = ({
  selectedPlan,
  selectedPlanInput,
  onClearPlan,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>({
    simConnected: false,
    sessionType: null,
    trackName: null,
    lastUpdate: ''
  });
  const [sessionData, setSessionData] = useState<SessionData>({});
  const [laps, setLaps] = useState<LapData[]>([]);
  const [avgLapTime, setAvgLapTime] = useState<number | null>(null);
  const [avgFuelPerLap, setAvgFuelPerLap] = useState<number | null>(null);
  
  // Telemetry source: 'auto' tries local exe first, then dev server
  const [telemetrySource, setTelemetrySource] = useState<'auto' | 'local' | 'dev'>('auto');
  const [activeSource, setActiveSource] = useState<string>('detecting...');
  const [localPort, setLocalPort] = useState<number>(8099);
  
  // Race configuration state
  const [raceDuration, setRaceDuration] = useState<number>(360); // 6 hours default
  const [fuelCapacity, setFuelCapacity] = useState<number>(120);
  const [pitStopTime, setPitStopTime] = useState<number>(60);
  const [pitStopFuelTime, setPitStopFuelTime] = useState<number>(2.5);
  const [driverSwapTime, setDriverSwapTime] = useState<number>(0);
  const [tireChangeTime, setTireChangeTime] = useState<number>(0);
  const [repairTime, setRepairTime] = useState<number>(0);
  const [formationLap, setFormationLap] = useState<boolean>(false);
  
  // Strategy calculator
  const strategyCalculator = useMemo(() => StrategyCalculatorFactory.create(), []);

  // Calculate averages when laps change
  useEffect(() => {
    if (laps.length === 0) {
      setAvgLapTime(null);
      setAvgFuelPerLap(null);
      return;
    }

    // Calculate average lap time
    // Skip lap 1 (formation/out-lap) to avoid skewing averages
    const validLapTimes = laps.filter(lap => lap.lapTime !== null && lap.lapTime > 0 && lap.lapNumber > 1);
    if (validLapTimes.length > 0) {
      const totalTime = validLapTimes.reduce((sum, lap) => sum + (lap.lapTime || 0), 0);
      setAvgLapTime(totalTime / validLapTimes.length);
    }

    // Calculate average fuel consumption per lap
    // Skip the delta that includes the formation lap (transition into lap 3+)
    if (laps.length >= 2) {
      const fuelConsumptions: number[] = [];
      for (let i = 1; i < laps.length; i++) {
        if (laps[i].lapNumber <= 2) continue; // skip formation lap fuel delta
        const prevFuel = laps[i - 1].fuelLevel;
        const currentFuel = laps[i].fuelLevel;
        
        if (prevFuel !== null && currentFuel !== null && prevFuel > currentFuel) {
          fuelConsumptions.push(prevFuel - currentFuel);
        }
      }
      
      if (fuelConsumptions.length > 0) {
        const totalFuel = fuelConsumptions.reduce((sum, fuel) => sum + fuel, 0);
        setAvgFuelPerLap(totalFuel / fuelConsumptions.length);
      }
    }
  }, [laps]);

  // Calculate pit stop details
  const pitStopDetails = useMemo((): PitStopDetails => {
    const fuelingTime = fuelCapacity * pitStopFuelTime; // total fueling time
    return {
      pitInTime: pitStopTime / 2,      // split pit lane time into in/out
      fuelingTime: fuelingTime,
      pitOutTime: pitStopTime / 2,
      refuelTime: pitStopFuelTime,      // seconds per liter
      totalTime: pitStopTime + fuelingTime,
    };
  }, [pitStopTime, pitStopFuelTime, fuelCapacity]);

  // Calculate race strategy when telemetry data updates
  const normalStrategy = useMemo((): Strategy | null => {
    if (!avgLapTime || !avgFuelPerLap || laps.length < 2) {
      return null;
    }

    // Create race event from telemetry data
    const raceEvent: RaceEvent = {
      track: {
        id: "live",
        name: sessionStatus.trackName || "Unknown Track",
        location: "Unknown",
        length: 0,
        turns: 0,
      },
      duration: raceDuration,
      carClasses: [{
        id: "custom",
        name: "Custom",
        category: "GT3",
        fuelCapacity: fuelCapacity,
        avgFuelConsumption: avgFuelPerLap,
        avgLapTime: avgLapTime,
      }],
    };

    // Create driver data from telemetry
    const driverData: DriverData = {
      avgLapTime: avgLapTime,
      fuelConsumption: avgFuelPerLap,
      fuelCapacity: fuelCapacity,
      pitStopDetails: pitStopDetails,
    };

    // Calculate strategy
    const strategy = strategyCalculator.calculateStrategy(
      raceEvent,
      driverData,
      "custom"
    );

    return strategy;
  }, [avgLapTime, avgFuelPerLap, laps.length, raceDuration, fuelCapacity, pitStopDetails, formationLap, sessionStatus.trackName, strategyCalculator]);

  // Fuel save strategy
  const fuelSaveStrategy = useMemo((): Strategy | null => {
    if (!normalStrategy || !normalStrategy.fuelSave) {
      return null;
    }
    return normalStrategy.fuelSave.adjustedStrategy;
  }, [normalStrategy]);

  const connectWebSocket = useCallback(() => {
    let resolvedSource: string | null = null;

    const tryFetch = async (url: string): Promise<any> => {
      const response = await fetch(url + (url.includes('?') ? '&' : '?') + 't=' + Date.now());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    };

    // Poll the telemetry API endpoint
    const pollInterval = setInterval(async () => {
      try {
        let data: any = null;
        const localUrl = `http://localhost:${localPort}`;
        const devUrl = '/api/telemetry';

        if (telemetrySource === 'local') {
          data = await tryFetch(localUrl);
          resolvedSource = 'local exe';
        } else if (telemetrySource === 'dev') {
          data = await tryFetch(devUrl);
          resolvedSource = 'dev server';
        } else {
          // Auto: try local exe first, fall back to dev server
          try {
            data = await tryFetch(localUrl);
            resolvedSource = 'local exe';
          } catch {
            data = await tryFetch(devUrl);
            resolvedSource = 'dev server';
          }
        }

        setActiveSource(resolvedSource || 'unknown');
          
        // Update status
        if (data.status) {
          setSessionStatus(data.status);
          setIsConnected(data.status.simConnected);
        }
        
        // Update session data (race duration, fuel capacity, etc.)
        if (data.session) {
          setSessionData(data.session);
        }
        
        // Update laps if there are new ones
        if (data.laps && data.laps.length > laps.length) {
          setLaps(data.laps);
        }
      } catch (error) {
        console.error('Error reading telemetry data:', error);
        setIsConnected(false);
        setActiveSource('not connected');
      }
    }, 1000); // Poll every second

    console.log('Started polling telemetry API');

    return () => {
      clearInterval(pollInterval);
    };
  }, [laps.length, telemetrySource, localPort]);

  useEffect(() => {
    const cleanup = connectWebSocket();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [connectWebSocket]);

  const clearData = () => {
    setLaps([]);
    setAvgLapTime(null);
    setAvgFuelPerLap(null);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Live Telemetry
          </h1>
          <p className="text-slate-400 text-lg">
            Real-time iRacing Data
          </p>
        </div>

        {/* Connection Status */}
        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
          <h2 className="text-xl font-semibold mb-4">Connection Status</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <span className="text-sm">
                Telemetry: <span className="font-semibold">{isConnected ? 'Connected' : 'Disconnected'}</span>
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${sessionStatus.simConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <span className="text-sm">
                iRacing: <span className="font-semibold">{sessionStatus.simConnected ? 'Connected' : 'Not Running'}</span>
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${activeSource === 'local exe' ? 'bg-blue-500' : activeSource === 'dev server' ? 'bg-yellow-500' : 'bg-slate-500'}`}></div>
              <span className="text-sm">
                Source: <span className="font-semibold">{activeSource}</span>
              </span>
            </div>
          </div>

          {/* Telemetry Source Settings */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm text-slate-400">Data source:</span>
              <div className="flex gap-2">
                {(['auto', 'local', 'dev'] as const).map((src) => (
                  <button
                    key={src}
                    onClick={() => setTelemetrySource(src)}
                    className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                      telemetrySource === src
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {src === 'auto' ? 'Auto Detect' : src === 'local' ? 'Local Exe' : 'Dev Server'}
                  </button>
                ))}
              </div>
              {telemetrySource !== 'dev' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Port:</span>
                  <input
                    type="number"
                    value={localPort}
                    onChange={(e) => setLocalPort(parseInt(e.target.value) || 8099)}
                    className="w-20 px-2 py-1 text-xs bg-slate-700 border border-slate-600 rounded text-white"
                  />
                </div>
              )}
            </div>
            {activeSource === 'not connected' && (
              <p className="mt-2 text-xs text-amber-400">
                Not receiving telemetry data. Make sure iRacingTelemetry.exe is running on your PC.
              </p>
            )}
          </div>

          {sessionStatus.trackName && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-400">Track:</span>{' '}
                  <span className="font-semibold">{sessionStatus.trackName}</span>
                </div>
                <div>
                  <span className="text-slate-400">Session:</span>{' '}
                  <span className="font-semibold">{sessionStatus.sessionType || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Session Information from Telemetry */}
        {sessionData.raceDurationMin && sessionData.raceDurationMin > 0 && (
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
            <h2 className="text-xl font-semibold mb-4">📊 Session Information</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-slate-400">Race Duration</div>
                <div className="text-lg font-bold">{sessionData.raceDurationMin.toFixed(0)} min</div>
              </div>
              <div>
                <div className="text-slate-400">Fuel Capacity</div>
                <div className="text-lg font-bold">{sessionData.fuelCapacity?.toFixed(1)} L</div>
              </div>
              <div>
                <div className="text-slate-400">Race Type</div>
                <div className="text-lg font-bold">{sessionData.isMulticlass ? 'Multi-class' : 'Single-class'}</div>
              </div>
              {sessionData.yourCarClassShortName && (
                <div>
                  <div className="text-slate-400">Your Class</div>
                  <div className="text-lg font-bold">{sessionData.yourCarClassShortName}</div>
                </div>
              )}
              {sessionData.sessionTimeRemain !== null && sessionData.sessionTimeRemain !== undefined && (
                <div>
                  <div className="text-slate-400">Time Remaining</div>
                  <div className="text-lg font-bold">{formatLapTime(sessionData.sessionTimeRemain)}</div>
                </div>
              )}
              {sessionData.isMulticlass && sessionData.p1LapTime && (
                <div>
                  <div className="text-slate-400">P1 Last Lap</div>
                  <div className="text-lg font-bold">{formatLapTime(sessionData.p1LapTime)}</div>
                </div>
              )}
              {sessionData.p1AvgLapTime && (
                <div>
                  <div className="text-slate-400">P1 Avg Lap Time</div>
                  <div className="text-lg font-bold text-blue-400">{formatLapTime(sessionData.p1AvgLapTime)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Race Configuration */}
        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
          <h2 className="text-xl font-semibold mb-4">⚙️ Race Configuration</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Race Duration */}
            <NumberInput
              label="Race Duration (minutes)"
              value={raceDuration}
              onChange={setRaceDuration}
              min={1}
              max={1440}
              step={30}
            />

            {/* Fuel Capacity */}
            <NumberInput
              label="Fuel Capacity (L)"
              value={fuelCapacity}
              onChange={setFuelCapacity}
              min={10}
              max={200}
              step={5}
            />

            {/* Formation Lap */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="formationLap"
                checked={formationLap}
                onChange={(e) => setFormationLap(e.target.checked)}
                className="w-4 h-4 text-green-600 bg-slate-700 border-slate-600 rounded focus:ring-green-500"
              />
              <label htmlFor="formationLap" className="text-sm font-medium">
                Formation Lap
              </label>
            </div>
          </div>

          {/* Pit Stop Details */}
          <div className="mt-6 pt-6 border-t border-slate-700">
            <h3 className="text-lg font-semibold mb-4">Pit Stop Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <NumberInput
                label="Base Pit Stop Time (s)"
                value={pitStopTime}
                onChange={setPitStopTime}
                min={0}
                max={120}
                step={1}
              />
              <NumberInput
                label="Refuel Time per L (s)"
                value={pitStopFuelTime}
                onChange={setPitStopFuelTime}
                min={0}
                max={10}
                step={0.1}
              />
              <NumberInput
                label="Driver Swap Time (s)"
                value={driverSwapTime}
                onChange={setDriverSwapTime}
                min={0}
                max={60}
                step={1}
              />
              <NumberInput
                label="Tire Change Time (s)"
                value={tireChangeTime}
                onChange={setTireChangeTime}
                min={0}
                max={120}
                step={1}
              />
              <NumberInput
                label="Repair Time (s)"
                value={repairTime}
                onChange={setRepairTime}
                min={0}
                max={300}
                step={1}
              />
            </div>
          </div>
        </div>

        {/* Live Plan Tracker — shows progress vs selected pre-race plan */}
        {selectedPlan && selectedPlanInput && (
          <div className="mb-6">
            <LivePlanTrackerPanel
              selectedPlan={selectedPlan}
              planInput={selectedPlanInput}
              currentLap={laps.length}
              currentFuel={laps.length > 0 ? laps[laps.length - 1].fuelLevel : null}
              avgLapTime={avgLapTime}
              avgFuelPerLap={avgFuelPerLap}
              laps={laps}
              raceDurationMinutes={raceDuration}
              onClearPlan={onClearPlan}
            />
          </div>
        )}

        {/* Strategy Results */}
        {normalStrategy && (
          <div className="space-y-6">
            <StrategyResult 
              strategy={normalStrategy}
              title="Normal Strategy (Live Telemetry)"
              fuelCapacity={fuelCapacity}
              pitStopDetails={pitStopDetails}
            />
            
            {fuelSaveStrategy && (
              <StrategyResult 
                strategy={fuelSaveStrategy}
                title="Fuel Save Strategy"
                fuelCapacity={fuelCapacity}
                pitStopDetails={pitStopDetails}
              />
            )}
          </div>
        )}

        {/* Race Strategy Optimizer — non-linear per-stint planner */}
        {avgLapTime && avgFuelPerLap && laps.length >= 4 && (
          <div className="mb-6">
            <RaceStrategyPanel
              laps={laps}
              currentLap={laps.length}
              currentFuel={laps.length > 0 ? laps[laps.length - 1].fuelLevel : null}
              avgLapTime={avgLapTime}
              avgFuelPerLap={avgFuelPerLap}
              raceDurationMinutes={raceDuration}
              fuelCapacity={fuelCapacity}
              isMultiClass={sessionData.isMulticlass}
              fastestClassLapTime={sessionData.p1AvgLapTime}
              carClass={sessionData.yourCarClassShortName}
              pitStopDetails={pitStopDetails}
            />
          </div>
        )}

        {/* Live Strategy Options — ranked comparison of all viable strategies */}
        {avgLapTime && avgFuelPerLap && laps.length >= 2 && (
          <div className="mb-6">
            <LiveStrategyOptions
              avgLapTime={avgLapTime}
              avgFuelPerLap={avgFuelPerLap}
              currentFuel={laps.length > 0 ? laps[laps.length - 1].fuelLevel : null}
              currentLap={laps.length}
              raceDurationMinutes={raceDuration}
              fuelCapacity={fuelCapacity}
              isMultiClass={sessionData.isMulticlass}
              fastestClassLapTime={sessionData.p1AvgLapTime}
              carClass={sessionData.yourCarClassShortName as any}
              fuelSavingCalibration={selectedPlanInput?.fuelSavingCalibration}
            />
          </div>
        )}

        {/* Averages Display */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
            <h3 className="text-sm text-slate-400 mb-2">Average Lap Time</h3>
            <div className="text-4xl font-bold text-blue-400">
              {formatLapTime(avgLapTime)}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Based on {laps.filter(l => l.lapTime !== null && l.lapTime > 0).length} laps
            </div>
          </div>

          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
            <h3 className="text-sm text-slate-400 mb-2">Average Fuel Per Lap</h3>
            <div className="text-4xl font-bold text-yellow-400">
              {avgFuelPerLap !== null ? `${avgFuelPerLap.toFixed(2)} L` : '--'}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Based on {Math.max(0, laps.length - 1)} lap intervals
            </div>
          </div>
        </div>

        {/* Lap History */}
        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Lap History</h2>
            {laps.length > 0 && (
              <button
                onClick={clearData}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-colors"
              >
                Clear Data
              </button>
            )}
          </div>

          {laps.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <p className="text-lg mb-2">No lap data yet</p>
              <p className="text-sm">
                {sessionStatus.simConnected 
                  ? 'Complete a lap in iRacing to see data here'
                  : 'Start iRacing and enter a session to begin'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Lap</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Lap Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Fuel Level</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Fuel Used</th>
                  </tr>
                </thead>
                <tbody>
                  {laps.map((lap, index) => {
                    const prevFuel = index > 0 ? laps[index - 1].fuelLevel : null;
                    const fuelUsed = prevFuel !== null && lap.fuelLevel !== null && prevFuel > lap.fuelLevel
                      ? prevFuel - lap.fuelLevel
                      : null;

                    return (
                      <tr key={index} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                        <td className="px-4 py-3 font-mono text-sm">{lap.lapNumber}</td>
                        <td className="px-4 py-3 font-mono text-sm">{formatLapTime(lap.lapTime)}</td>
                        <td className="px-4 py-3 font-mono text-sm">{formatFuel(lap.fuelLevel)} L</td>
                        <td className="px-4 py-3 font-mono text-sm">
                          {fuelUsed !== null ? `${fuelUsed.toFixed(2)} L` : '--'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-400 mb-2">ℹ️ How It Works</h3>
          <ul className="text-sm text-slate-300 space-y-1">
            <li>• Start the Python telemetry service (python iracing_telemetry.py)</li>
            <li>• Launch iRacing and enter a session (practice, race, qualifying)</li>
            <li>• All data is automatically captured: race duration, fuel capacity, P1 lap times, your lap data</li>
            <li>• Live strategy calculations update every lap</li>
            <li>• In multi-class races, P1 (fastest class leader) determines race length</li>
            <li>• No manual input needed - everything comes from iRacing telemetry!</li>
          </ul>
        </div>

        {/* Debug Panel - Raw Data Comparison */}
        <details className="mt-6 bg-slate-900 border border-slate-700 rounded-lg p-4">
          <summary className="cursor-pointer font-semibold text-yellow-400 mb-2">
            🔍 Debug: Raw Telemetry Data (Click to expand)
          </summary>
          <div className="mt-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-400 mb-2">Session Status:</h4>
              <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto">
                {JSON.stringify(sessionStatus, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-400 mb-2">Lap Data Array ({laps.length} laps):</h4>
              <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto max-h-96 overflow-y-auto">
                {JSON.stringify(laps, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-400 mb-2">Calculated Averages:</h4>
              <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto">
{`Average Lap Time: ${avgLapTime !== null ? avgLapTime.toFixed(3) + 's' : 'null'}
Average Fuel/Lap: ${avgFuelPerLap !== null ? avgFuelPerLap.toFixed(3) + 'L' : 'null'}
Valid Laps Count: ${laps.filter(l => l.lapTime !== null && l.lapTime > 0).length}
Fuel Intervals: ${Math.max(0, laps.length - 1)}`}
              </pre>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};
